import { useTrack, TrackEvents } from '@/composables/useTrack';
import { useToast } from '@/composables/useToast';
import { useIntegrationStore } from '../store/useIntegrationStore';
import type { StaticIntegration } from '../store/useIntegrationStore';
import { paragon } from '@useparagon/connect';

/**
 * Composable for integration-specific actions and event handling
 * Handles UI interactions, tracking, and user feedback
 */
export const useIntegrationActions = () => {
  const integrationStore = useIntegrationStore();
  const { track } = useTrack();
  const { success } = useToast();

  const toggleIntegration = async (integrationName: string) => {
    try {
      await paragon.connect(integrationName, {
        onSuccess: () => {
          track(TrackEvents.INTEGRATIONS_SETUP, {
            app: integrationName,
            coming_soon: false,
            setup_requested: false,
          });
          success(`${integrationName} integration updated successfully`);
          integrationStore.fetchIntegrations(true);
        },
        onError: (error: any) => {
          console.error('Integration error:', error);
        },
      });
    } catch (error) {
      console.error('Failed to toggle integration:', error);
    }
  };

  const handleRequestEnable = (integration: StaticIntegration) => {
    track(TrackEvents.INTEGRATIONS_SETUP, {
      app: integration.name,
      coming_soon: false,
      setup_requested: true,
    });
    success(`Thanks for your interest in ${integration.name}! We've recorded your request and will notify you when it becomes available.`, {
      title: 'Integration Request Submitted',
    });
  };

  const handleStaticIntegrationClick = (integration: StaticIntegration, event: MouseEvent) => {
    if (integration.setupFlow === 'assisted') {
      event.preventDefault();
      track(TrackEvents.INTEGRATIONS_SETUP, {
        app: integration.name,
        coming_soon: false,
        setup_requested: true,
      });

      const intercom = (window as Window & { Intercom?: (command: string, message?: string) => void }).Intercom;
      if (typeof intercom === 'function') {
        intercom('showNewMessage', integration.assistedSetup.supportMessage);
        success(integration.assistedSetup.toastMessage, {
          title: integration.assistedSetup.toastTitle,
        });
      } else {
        window.open('https://zight.com/contact/', '_blank', 'noopener,noreferrer');
      }
      return;
    }

    track(TrackEvents.INTEGRATIONS_SETUP, {
      app: integration.name,
      coming_soon: false,
      setup_requested: false,
    });
  };

  return {
    toggleIntegration,
    handleRequestEnable,
    handleStaticIntegrationClick,
  };
};
